import { toFormat } from "@/lib/dateHelpers";
import {
  getStatusTagColor,
  getCompletionLabel,
  filterAndSortRequirements
} from "@/features/person/utils/pgm-requirements";
import { getRequireTypeLabel } from "@/features/person/utils/education-requirement-reason-codes";
import type { EducationCertification } from "@/types";
import type { ProgramDetailRow, ProgramProgressRow } from "../PGMProgramTable/utils";

export const formatCertificationProgramData = (
  certificationHistory: EducationCertification[]
): LifelongLearningProgressRow[] => {
  return certificationHistory.map((cert, index) => {
    const levelCode = cert.levelCode ?? "";
    const id = `${levelCode || cert.programName}-${cert.startDate}-${index}`;
    const sortedReqs = cert.programRequirements ? filterAndSortRequirements(cert.programRequirements, levelCode) : [];

    const requirementRows: ProgramDetailRow[] = sortedReqs.map((req) => {
      const statusLabel = getCompletionLabel(req.isCompleted, req.completionStatus);
      const statusType = getStatusTagColor(statusLabel);
      return {
        id: `${id}-req-${req.requireId}`,
        type: "Requirement",
        requireTypeLabel: getRequireTypeLabel(req.requirementDefinition?.requireType, req.levelCode),
        description: req.requirementDefinition?.description || "Unknown requirement",
        secondaryText: req.requireId,
        requireId: req.requireId,
        levelCode: req.levelCode,
        date: req.completionDate ? toFormat(req.completionDate) : null,
        completed: req.isCompleted,
        statusLabel,
        statusType,
        scoreText: req.score != null ? `${req.score}` : null,
        requirement: req,
        levelStartDate: cert.startDate,
        archived: cert.archived || req.archived
      };
    });

    const subscriptionEnd = cert.subscription?.endDate;
    const isExpired = cert.subscription?.expired ?? false;
    const archived = cert.archived ?? false;
    const completed = cert.completeCode === "COMPLETE";

    const levelLabel =
      cert.level != null
        ? cert.careerPath
          ? `Level ${cert.level} ${cert.careerPath}`
          : `Level ${cert.level}`
        : cert.programDescription;

    const status = archived ? "Archived" : completed ? "Complete" : "In Progress";

    return {
      id,
      archived,
      levelCode,
      level: levelLabel,
      code: levelCode || cert.programName,
      version: cert.curriculumVersion,
      stage: completed ? "Completed" : "Started",
      startDate: cert.startDate ? toFormat(cert.startDate) : null,
      endDate: cert.endDate ? toFormat(cert.endDate) : null,
      startDateIso: cert.startDate,
      endDateIso: cert.endDate ?? null,
      completeCode: cert.completeCode ?? null,
      remarks: cert.remarks ?? null,
      detailsCount: requirementRows.length,
      completedDetailsCount: requirementRows.filter((detail) => detail.completed).length,
      status,
      completed,
      expiresDate: subscriptionEnd ? toFormat(subscriptionEnd) : null,
      isExpired,
      details: requirementRows,
      careerPath: cert.careerPath,
      programName: cert.programName,
      programDescription: cert.programDescription
    };
  });
};

export type LifelongLearningProgressRow = ProgramProgressRow & {
  levelCode: string;
  careerPath: string;
  programName: string;
  programDescription: string;
};
